import { useState } from 'react';
import { TabBar } from '@/components/ui/TabBar';
import { useAuth } from '@/hooks/useAuth';
import LabDashboard from './LabDashboard';
import LabOrdersTab from './LabOrdersTab';
import SamplesTab from './SamplesTab';
import ResultsTab from './ResultsTab';
import LabReportsTab from './LabReportsTab';
import OutsideLabTab from './OutsideLabTab';

/**
 * The Laboratory module — the LIS's single screen, with the workflow as tabs.
 *
 * ── Why the tabs are not permission-gated one-to-one ─────────────
 *
 * Every tab here is READ-able by anyone holding the `laboratory` module,
 * because a receptionist telling a patient where their blood test is needs to
 * see the sample queue, and a technician needs to see the order behind the
 * tube. What is gated is the ACTIONS inside them — collect, receive, enter,
 * verify, release, amend — each on its own `lab:*` grant, checked
 * server-side on every request (routes/laboratory/*). Hiding a tab would give
 * less information and no more security.
 *
 * ── Where the masters went ───────────────────────────────────────
 *
 * Configuration (tests, parameters, reference ranges, sample types,
 * containers, packages) is NOT a tab here. It lives at **Admin Panel →
 * Laboratory**, beside the other master-data screens, because editing a
 * reference range changes what "normal" means for every future patient — a
 * governance act, not bench work. Its grant, `admin:laboratory`, was always an
 * `admin:*` one; the screen simply now sits where that already said it did.
 * See features/admin/laboratory/LabMastersTab.tsx.
 */
type LabTab = 'dashboard' | 'orders' | 'samples' | 'results' | 'outside' | 'reports';

// "Results" is the in-house bench (entry + verification). "Outside / Reference
// Lab" is the send-out desk. Verification of an outside result can be done from
// either — the Results tab's verification queue lists both, because
// verification is one job whoever ran the test. Reference laboratories
// themselves are configured in Admin Panel → Laboratory, with the other masters.
const TABS: Array<{ id: LabTab; label: string }> = [
  { id: 'dashboard', label: '📊 Dashboard' },
  { id: 'orders',    label: '📋 Orders' },
  { id: 'samples',   label: '🩸 Samples' },
  { id: 'results',   label: '🔬 Internal lab / Results' },
  { id: 'outside',   label: '🚚 Outside / Reference Lab' },
  { id: 'reports',   label: '📄 Reports' },
];

export default function LaboratoryModule() {
  const { user } = useAuth();
  const [tab, setTab] = useState<LabTab>('dashboard');
  // Carried from a dashboard tile into the tab it opens, so drilling into
  // "12 pending collection" lands on those twelve rather than on the default
  // queue — which is the difference between a dashboard and a poster.
  const [handoff, setHandoff] = useState<Record<string, string> | undefined>(undefined);

  const go = (target: { tab: 'orders' | 'samples' | 'results' | 'outside'; filter?: Record<string, string> }) => {
    setHandoff(target.filter);
    setTab(target.tab);
  };

  const switchTab = (next: LabTab) => {
    // A hand-off filter belongs to the navigation that set it; picking a tab
    // by hand starts clean rather than silently inheriting someone else's
    // filter and looking empty.
    setHandoff(undefined);
    setTab(next);
  };

  if (!user?.modules?.includes('laboratory')) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-[13px] text-muted-foreground">
        You don’t have access to the Laboratory module.
      </div>
    );
  }

  return (
    <div className="fade-in">
      <TabBar tabs={TABS} active={tab} onChange={switchTab} className="mb-4" />
      {tab === 'dashboard' && <LabDashboard onNavigate={go} />}
      {tab === 'orders'    && <LabOrdersTab key={JSON.stringify(handoff)} initialFilter={handoff} />}
      {tab === 'samples'   && <SamplesTab key={JSON.stringify(handoff)} initialFilter={handoff} />}
      {tab === 'results'   && <ResultsTab key={JSON.stringify(handoff)} initialFilter={handoff} />}
      {tab === 'outside'   && <OutsideLabTab key={JSON.stringify(handoff)} initialFilter={handoff} />}
      {tab === 'reports'   && <LabReportsTab />}
    </div>
  );
}
